import React, { useEffect } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Center,
  Button,
  Box,
  Icon,
  HStack,
  useColorModeValue as mode,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  ButtonGroup,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import { TbArrowBackUp, TbExternalLink, TbTrash } from "react-icons/tb";
import { useSession } from "next-auth/react";
import toast from "react-hot-toast";

// Components
import ProtectedRoute from "components/protected-route";
import { Page } from "components/page";
import AICarousel from "components/ai-carousel";
import Preview from "components/ai-carousel/preview";

// Styles
import { layerStyle } from "theme/names";

// Utils
import { getCarouselsList, deleteCarousel } from "controllers/carousel";
import { getAccount } from "utils/sessionHelper";

// Types
import { StateWithId as CarouselState } from "types/carousel";
import { useRouter } from "next/router";
import Head from "next/head";

const DeleteButton = ({ carouselId, setCarousels }) => {
  const { data: session } = useSession() ?? {};
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [isDeleting, setisDeleting] = React.useState<boolean>(false);
  const handleDeleteCarousel = async () => {
    setisDeleting(true);
    try {
      await deleteCarousel(carouselId, getAccount(session).id);
      setCarousels((prev) => prev.filter((c) => c.id !== carouselId));
    } catch (e) {
      toast.error("Something went wrong with deleting the carousel");
    }
    setisDeleting(false);
    onClose();
  };
  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom">
      <PopoverTrigger>
        <Button
          variant="actionDestructive"
          size="sm"
          leftIcon={<Icon as={TbTrash} />}
          color={mode("gray.700", "white")}
          onClick={onToggle}
        >
          Delete
        </Button>
      </PopoverTrigger>
      <PopoverContent bg={mode("neutral.lightMode.neutral", "neutral.darkMode.neutral")}>
        <PopoverHeader fontWeight="semibold">Confirmation</PopoverHeader>
        <PopoverArrow bg={mode("neutral.lightMode.neutral", "neutral.darkMode.neutral")} />
        <PopoverCloseButton />
        <PopoverBody>
          Are you sure you want to continue with your action?
        </PopoverBody>
        <PopoverFooter display="flex" justifyContent="flex-end">
          <ButtonGroup size="sm">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteCarousel}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          </ButtonGroup>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

const MyCarousels = ({ carousels, setCarousels }) => {
  const [selectedCarousel, setselectedCarousel] =
    React.useState<CarouselState>();
  if (carousels.length === 0) {
    return (
      <Center pt={10} h="200px">
        No saved carousels found
      </Center>
    );
  }
  if (selectedCarousel) {
    return (
      <>
        <Box mb={4}>
          <Button
            onClick={() => setselectedCarousel(undefined)}
            variant="transparent"
            leftIcon={<Icon as={TbArrowBackUp} />}
          >
            Back
          </Button>
        </Box>
        <AICarousel state={selectedCarousel} setCarousels={setCarousels} />
      </>
    );
  }
  return (
    <SimpleGrid columns={4} gap={4}>
      {carousels.map((data) => (
        <VStack
          key={data.id}
          p={6}
          layerStyle={layerStyle["bg.border.rounded"]}
        >
          <Preview
            content={data.carousels?.[0]}
            settings={data.settings}
            theme={data.theme}
            slideNo={0}
            scale={0.47}
          />
          <HStack justifyContent="center" w="full" mt={4}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setselectedCarousel(data)}
              leftIcon={<Icon as={TbExternalLink} />}
            >
              Open
            </Button>
            <DeleteButton carouselId={data.id} setCarousels={setCarousels} />
          </HStack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

export default function CarouselGenerator(_props: any) {
  const router = useRouter();
  const [selectedTabIdx, setselectedTabIdx] = React.useState<number>(0);
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const [isSessionLoaded, setisSessionLoaded] = React.useState<boolean>(false);
  const [isfetchingCarousels, setisFetchingCarousels] =
    React.useState<boolean>(false);
  const [carousels, setCarousels] = React.useState<CarouselState[]>([]);
  useEffect(() => {
    const url = new URL(window.location.href);
    const tab = url.searchParams.get("tab");
    if (tab === "my-carousels") {
      setselectedTabIdx(1);
    } else {
      setselectedTabIdx(0);
    }
  }, [router.asPath]);
  useEffect(() => {
    const fetchCarousels = async () => {
      setisFetchingCarousels(true);
      try {
        const carouselsList = await getCarouselsList(getAccount(session).id);
        setCarousels(carouselsList);
      } catch (e) {
        toast.error("Something went wrong while fetching the carousels");
      }
      setisFetchingCarousels(false);
    };
    if (session?.user?.uid && !isSessionLoaded) {
      fetchCarousels();
      setisSessionLoaded(true);
    }
  }, [session, setisFetchingCarousels, setCarousels]);
  if (!(loading || session)) {
    return <ProtectedRoute />;
  }
  return (
    <Page
      forceCloseScheduler={true}
      title="Carousel Generator"
      titleIconWrapperProps={{ gap: 3 }}
      description="Create and schedule carousels easily. Use AI, YouTube videos or links to generate carousels in seconds."
      descriptionWrapperProps={{ pb: "5" }}
    >
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      </Head>
      <Tabs isLazy
        onChange={(index) => {
          setselectedTabIdx(index);
          if (index === 1) {
            router.push("/carousel-generator?tab=my-carousels");
          }
          else {
            router.push("/carousel-generator");
          }

        }}
        index={selectedTabIdx}
      >
        <TabList mb={4}>
          <Tab>+ Create New</Tab>
          <Tab>
            My Carousels {carousels.length > 0 && `[${carousels.length}]`}
          </Tab>
        </TabList>

        <TabPanels mb="10"
        >
          <TabPanel p={0}>
            <AICarousel setCarousels={setCarousels} />
          </TabPanel>
          <TabPanel>
            {isfetchingCarousels ? (
              <Center pt={10} h="200px">
                <PuffLoader
                  color={"#0A66C2"}
                  loading
                  cssOverride={{
                    display: "block",
                    margin: "0 auto",
                    borderColor: "red",
                  }}
                  size={50}
                />
              </Center>
            ) : (
              <MyCarousels carousels={carousels} setCarousels={setCarousels} />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Page>
  );
}
